import { useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import {
  horizontalListSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, {
  type ClipboardEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Avatar } from '@/lib/base/avatar/avatar';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { TagCloseX } from '@/lib/base/tags/tag-close-x';
import { useOpenSlideoutOptional } from '@/lib/crm/hooks/useOpenSlideout';
import { usePersonByEmail } from '@/lib/crm/hooks/usePersonByEmail';
import {
  type ContactResult,
  type EntityResult,
  useEntitySearch,
  type UserResult,
} from '@/lib/search';
import { shouldIgnoreOutsideInteraction } from '@/utils/third-party-editor-ui';

interface EmailRecipientsInputProps {
  recipients: string[];
  onChange: (recipients: string[]) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  fieldId: string;
  isDropTarget?: boolean;
  isEmpty?: boolean;
  isDragActive?: boolean;
  /** Org id — required for resolving recipient emails to CRM entities. */
  orgId?: number;
  /**
   * When true, recipient chips that resolve to a CRM person become clickable
   * and open that person's slideout. Opt-in (composer only) so the proposal
   * flow keeps plain, non-resolving chips.
   */
  linkRecipients?: boolean;
}

// A recipient chip label that resolves the email to a CRM person and, when
// resolved, becomes a button that opens that person's slideout. Rendered only
// when the composer opts in (`linkRecipients`) so it never fires per-chip
// lookups in the proposal flow.
const RecipientEntityLabel = React.memo(function RecipientEntityLabel({
  email,
  orgId,
}: {
  email: string;
  orgId: number;
}) {
  const { person } = usePersonByEmail(email, String(orgId));
  const openSlideout = useOpenSlideoutOptional();

  if (!person || !openSlideout) {
    return <span className='truncate'>{email}</span>;
  }

  return (
    <button
      type='button'
      // Stop the pointer-down from starting a chip drag (mirrors TagCloseX).
      onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
      onClick={() => openSlideout(person.entityId)}
      aria-label={`Open ${person.name}`}
      className='truncate cursor-pointer hover:underline'
    >
      {email}
    </button>
  );
});

interface SortableEmailChipProps {
  email: string;
  disabled: boolean;
  onRemove: () => void;
  orgId?: number;
  linkRecipients?: boolean;
}

function SortableEmailChip({
  email,
  disabled,
  onRemove,
  fieldId,
  orgId,
  linkRecipients,
}: SortableEmailChipProps & { fieldId: string }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: email, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  let cursorClassName = 'cursor-grab hover:cursor-grab';
  if (isDragging) {
    cursorClassName =
      'border border-dashed border-tertiary opacity-50 cursor-grabbing';
  } else if (disabled) {
    cursorClassName = '';
  }

  return (
    <span
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      aria-describedby={`email-drag-instructions-${fieldId}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 bg-secondary text-sm text-secondary rounded-md max-w-full ${cursorClassName}`}
    >
      {linkRecipients && orgId !== undefined ? (
        <RecipientEntityLabel email={email} orgId={orgId} />
      ) : (
        <span className='truncate'>{email}</span>
      )}
      {!disabled && (
        <TagCloseX
          size='sm'
          onPress={onRemove}
          onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
          className='cursor-pointer'
        />
      )}
    </span>
  );
}

// Basic email validation
const isValidEmail = (email: string): boolean => {
  const trimmed = email.trim();
  return trimmed.includes('@') && trimmed.includes('.') && trimmed.length > 5;
};

// Parse pasted text into email addresses
const parseEmails = (text: string): string[] => {
  // Split by comma, semicolon, space, or newline
  return text
    .split(/[,;\s\n]+/)
    .map(email => email.trim())
    .filter(email => email.length > 0);
};

// Type guard to check if entity has an email field
function hasEmail(entity: EntityResult): entity is UserResult | ContactResult {
  return entity.type === 'ahoy_user' || entity.type === 'ahoy_contact';
}

// Minimum characters to trigger search
const MIN_QUERY_LENGTH = 2;

export default function EmailRecipientsInput({
  recipients,
  onChange,
  label,
  placeholder = 'Add recipients',
  disabled = false,
  fieldId,
  isDropTarget = false,
  isEmpty = false,
  isDragActive = false,
  orgId,
  linkRecipients = false,
}: EmailRecipientsInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { setNodeRef, isOver } = useDroppable({ id: fieldId });

  // Search for users and contacts (entities with emails)
  // Memoize types array to prevent re-renders from triggering new searches
  const searchTypes = useMemo(
    (): ('ahoy_user' | 'ahoy_contact')[] => ['ahoy_user', 'ahoy_contact'],
    []
  );
  const searchEnabled = inputValue.length >= MIN_QUERY_LENGTH && !disabled;
  const {
    results,
    isLoading,
    error: searchError,
  } = useEntitySearch(inputValue, {
    types: searchTypes,
    enabled: searchEnabled,
    // Include hidden contacts so auto-created stub contacts (inbound email
    // senders, calendar attendees) are reachable when composing email — even
    // though they're filtered from the primary contact list view.
    includeHidden: true,
  });

  // Flatten and filter results to only entities with emails
  const searchResults = useCallback((): (UserResult | ContactResult)[] => {
    if (!results) {
      return [];
    }
    const allResults: EntityResult[] = [
      ...(results.users || []),
      ...(results.contacts || []),
    ];
    // Filter to entities with email and exclude already-added emails
    return allResults.filter(
      (entity): entity is UserResult | ContactResult =>
        hasEmail(entity) && !recipients.includes(entity.email)
    );
  }, [results, recipients]);

  const flatResults = searchResults();

  // Show dropdown when:
  // - User is actively typing (dropdown open + min chars)
  // - AND (loading OR has results OR finished loading with no results to show "no results" message)
  // Don't show if there was a search error (likely no orgId or API issue)
  const hasFinishedSearching = !isLoading && results !== null;
  const shouldShowDropdown =
    isDropdownOpen &&
    inputValue.length >= MIN_QUERY_LENGTH &&
    !searchError &&
    (isLoading || flatResults.length > 0 || hasFinishedSearching);

  // Reset highlighted index when results change
  useEffect(() => {
    setHighlightedIndex(flatResults.length > 0 ? 0 : -1);
  }, [flatResults.length]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (shouldIgnoreOutsideInteraction(event.target)) {
        return;
      }
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle selecting an entity from search results
  const handleSelectEntity = useCallback(
    (entity: UserResult | ContactResult) => {
      if (!recipients.includes(entity.email)) {
        onChange([...recipients, entity.email]);
      }
      setInputValue('');
      setIsDropdownOpen(false);
      setError(null);
      inputRef.current?.focus();
    },
    [recipients, onChange]
  );

  const showDropHighlight = isOver || isDropTarget;
  const showMutedStyle = isDragActive && isEmpty;

  const addEmail = (email: string) => {
    const trimmed = email.trim();

    if (!trimmed) {
      return;
    }

    if (!isValidEmail(trimmed)) {
      setError('Invalid email format');
      return;
    }

    if (recipients.includes(trimmed)) {
      setError('Email already added');
      return;
    }

    onChange([...recipients, trimmed]);
    setInputValue('');
    setError(null);
  };

  const removeEmail = (index: number) => {
    onChange(recipients.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Handle dropdown navigation when dropdown is open
    if (shouldShowDropdown && flatResults.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < flatResults.length - 1 ? prev + 1 : 0
        );
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : flatResults.length - 1
        );
        return;
      }
      if (e.key === 'Enter' && highlightedIndex >= 0) {
        e.preventDefault();
        handleSelectEntity(flatResults[highlightedIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsDropdownOpen(false);
        return;
      }
    }

    // Add email on Enter, Comma, or Semicolon
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
      e.preventDefault();
      addEmail(inputValue);
      return;
    }

    // Remove last chip on Backspace if input is empty
    if (e.key === 'Backspace' && inputValue === '' && recipients.length > 0) {
      removeEmail(recipients.length - 1);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pastedText = e.clipboardData.getData('text');
    const emails = parseEmails(pastedText);

    // If multiple emails were pasted, process them
    if (emails.length > 1) {
      e.preventDefault();

      // Filter for valid emails, remove duplicates with existing recipients AND within pasted list
      const seen = new Set(recipients);
      const validEmails = emails.filter(email => {
        if (!isValidEmail(email)) {
          return false;
        }
        if (seen.has(email)) {
          return false;
        }
        seen.add(email); // Track this email to prevent duplicates within the paste
        return true;
      });

      if (validEmails.length > 0) {
        onChange([...recipients, ...validEmails]);
        setInputValue('');
        setError(null);
      } else {
        setError('No valid new emails to add');
      }
    }
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
    if (error) {
      setError(null);
    }
    // Open dropdown when typing
    if (value.length >= MIN_QUERY_LENGTH) {
      setIsDropdownOpen(true);
    } else {
      setIsDropdownOpen(false);
    }
  };

  const handleInputFocus = () => {
    if (inputValue.length >= MIN_QUERY_LENGTH) {
      setIsDropdownOpen(true);
    }
  };

  const highlightClassName = showDropHighlight
    ? 'ring-2 ring-brand-solid bg-brand-solid/5 border-brand-solid'
    : 'border-primary focus-within:ring-2 focus-within:ring-brand-solid focus-within:border-brand-solid';

  const containerClassName = [
    'flex-1 min-w-0 flex flex-wrap items-center gap-1.5 px-2 py-1.5 bg-primary border rounded-lg transition-all',
    highlightClassName,
    showMutedStyle ? 'border-dashed opacity-60' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className='flex flex-col gap-1.5'>
      <span id={`email-drag-instructions-${fieldId}`} className='sr-only'>
        Drag to move between fields, or press Space to pick up
      </span>
      <div className='flex items-center gap-1.5'>
        {label && (
          <span className='text-sm font-medium text-secondary shrink-0'>
            {label}:
          </span>
        )}

        <div className='relative flex-1 min-w-0' ref={containerRef}>
          <div
            ref={setNodeRef}
            className={containerClassName}
            onClick={() => inputRef.current?.focus()}
          >
            <SortableContext
              items={recipients}
              strategy={horizontalListSortingStrategy}
            >
              {recipients.map((email, index) => (
                <SortableEmailChip
                  key={email}
                  email={email}
                  disabled={disabled}
                  onRemove={() => removeEmail(index)}
                  fieldId={fieldId}
                  orgId={orgId}
                  linkRecipients={linkRecipients}
                />
              ))}
            </SortableContext>

            <input
              ref={inputRef}
              type='text'
              value={inputValue}
              onChange={e => handleInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onFocus={handleInputFocus}
              placeholder={recipients.length === 0 ? placeholder : ''}
              disabled={disabled}
              className='flex-1 min-w-[80px] outline-none bg-transparent text-sm text-primary placeholder:text-tertiary disabled:cursor-not-allowed'
              role='combobox'
              aria-expanded={shouldShowDropdown}
              aria-controls={`${fieldId}-listbox`}
              aria-activedescendant={
                shouldShowDropdown && highlightedIndex >= 0
                  ? `${fieldId}-option-${highlightedIndex}`
                  : undefined
              }
              autoComplete='off'
            />
          </div>

          {/* Search results dropdown */}
          {shouldShowDropdown && (
            <div
              ref={dropdownRef}
              id={`${fieldId}-listbox`}
              role='listbox'
              className='absolute left-0 right-0 top-full z-50 mt-1 max-h-60 overflow-auto rounded-lg border border-primary bg-primary shadow-lg'
            >
              {isLoading && (
                <div className='flex items-center gap-2 px-3 py-2'>
                  <div className='size-6 flex items-center justify-center'>
                    <LoadingIndicator inline size='xs' />
                  </div>
                  <span className='text-sm text-fg-quaternary'>Searching…</span>
                </div>
              )}
              {!isLoading && flatResults.length > 0 && (
                <>
                  {flatResults.map((entity, index) => {
                    const avatarContent = entity.avatar_url ? (
                      <Avatar
                        size='xs'
                        src={entity.avatar_url}
                        alt={entity.name}
                      />
                    ) : (
                      <div className='flex size-6 items-center justify-center rounded-full bg-tertiary text-xs font-medium text-secondary'>
                        {entity.name.charAt(0).toUpperCase()}
                      </div>
                    );

                    return (
                      <div
                        key={`${entity.type}-${entity.id}`}
                        id={`${fieldId}-option-${index}`}
                        role='option'
                        aria-selected={highlightedIndex === index}
                        className={`flex cursor-pointer items-center gap-2 px-3 py-2 ${
                          highlightedIndex === index
                            ? 'bg-secondary'
                            : 'hover:bg-secondary'
                        }`}
                        onClick={() => handleSelectEntity(entity)}
                        onMouseEnter={() => setHighlightedIndex(index)}
                      >
                        {avatarContent}
                        <div className='flex min-w-0 flex-1 flex-col'>
                          <span className='truncate text-sm font-medium text-primary'>
                            {entity.name}
                          </span>
                          <span className='truncate text-xs text-tertiary'>
                            {entity.email}
                          </span>
                        </div>
                        <span className='shrink-0 rounded-md bg-tertiary px-1.5 py-0.5 text-xs text-secondary'>
                          {entity.type === 'ahoy_user' ? 'Users' : 'Contact'}
                        </span>
                      </div>
                    );
                  })}
                </>
              )}
              {!isLoading &&
                flatResults.length === 0 &&
                hasFinishedSearching && (
                  <div className='px-3 py-4 text-center text-sm text-tertiary'>
                    No contacts found. Press Enter to add as email.
                  </div>
                )}
            </div>
          )}
        </div>
      </div>

      {error && (
        <span className='text-xs text-utility-error-600 ml-[56px]'>
          {error}
        </span>
      )}
    </div>
  );
}
